/// <amd-module name="Feed/Connect/Components/GetToolPanel" />
import * as React from "react";
import { connect } from "react-redux";

import { FormatComponent } from "VSS/Platform/Components/Format";

import { Panel } from "VSSUI/Panel";
import { Tab, TabBar, TabSize } from "VSSUI/Tabs";

import * as Res from "Feed/Connect/Resources";
import { isMember } from "Feed/Core/Redux/FeedHub";

import { ConnectToFeedActions, getIsHosted, getSelectedTool, IConnectToFeedAwareState } from "../Redux/ConnectToFeed";
import { ITool, ToolId } from "../Tool";
import { CopyableCode, FWLink } from "./Common";
import { OperatingSystem } from "./ToolDetails";

interface IGetToolPanelProps {
    tool?: ITool;
    userIsMember: boolean;
    isHosted: boolean;
}

const Actions = {
    dismiss: () => ConnectToFeedActions.getTools(undefined)
};

const GetToolComponents: {
    [toolId: number]: (props: { userIsMember?: boolean; isHosted?: boolean }) => JSX.Element;
} = {};

function getGetPackagingToolComponent(tool: ITool, userIsMember: boolean, isHosted: boolean) {
    const GetToolDetails = GetToolComponents[tool.id];
    return GetToolDetails ? <GetToolDetails userIsMember={userIsMember} isHosted={isHosted} /> : null;
}

class UnconnectedGetToolPanel extends React.Component<IGetToolPanelProps & typeof Actions> {
    public render() {
        const { tool, userIsMember, isHosted } = this.props;
        return (
            tool && (
                <Panel
                    onDismiss={this.props.dismiss}
                    titleProps={{ text: Res.GetTools_Text }}
                    className="get-packaging-tools body-m"
                    footerButtonProps={[{ text: Res.ClosePanel, onClick: this.props.dismiss, primary: true }]}
                >
                    <div className="rhythm-vertical-20">
                        {getGetPackagingToolComponent(tool, userIsMember, isHosted)}
                    </div>
                </Panel>
            )
        );
    }
}

function mapStateToProps(state: IConnectToFeedAwareState): IGetToolPanelProps {
    return {
        tool: getSelectedTool(state),
        userIsMember: isMember(state),
        isHosted: getIsHosted(state) || false
    };
}

export const GetToolPanel = connect(mapStateToProps, Actions)(UnconnectedGetToolPanel);

GetToolComponents[ToolId.DotNet] = ({ userIsMember, isHosted }) => {
    if (userIsMember) {
        return (
            <React.Fragment>
                <div>
                    {isHosted && Res.GetTool_DotNet_Preamble}
                    {!isHosted && Res.GetTool_DotNet_OnPremPreamble}
                </div>

                <div>
                    <div className="title-xs">{Res.Step} 1</div>
                    <p>
                        <FWLink id="2103972">{Res.InstallDotNetCore} (2.1.400+)</FWLink>
                    </p>
                </div>

                {isHosted && (
                    <div>
                        <div className="title-xs">{Res.Step} 2</div>
                        <p>
                            <FWLink id="2099625">{Res.InstallCredProvider}</FWLink>
                        </p>
                    </div>
                )}
            </React.Fragment>
        );
    } else {
        return (
            <React.Fragment>
                <div>
                    {Res.GetTool_DotNet_Preamble}
                    <p>
                        <FWLink id="2103972">{Res.InstallDotNetCore}</FWLink>
                    </p>
                </div>
            </React.Fragment>
        );
    }
};

GetToolComponents[ToolId.NuGet] = ({ userIsMember, isHosted }) => {
    if (userIsMember) {
        return (
            <React.Fragment>
                <div>
                    {isHosted ? (
                        <React.Fragment>
                            {Res.GetTool_NuGet_Preamble}
                            <p>{Res.GetTool_NuGet_VisualStudioInstalled}</p>
                            <p>
                                <FormatComponent format={Res.GetTool_NuGet_PreambleNote}>
                                    <FWLink underline id="2104208">
                                        {Res.OnlineDocumentation}
                                    </FWLink>
                                </FormatComponent>
                            </p>
                        </React.Fragment>
                    ) : (
                        Res.GetTool_NuGet_OnPremPreamble
                    )}
                </div>

                <div>
                    <div className="title-xs">{Res.Step} 1</div>
                    <p>
                        <FWLink id="2099732">{Res.DownloadNuGet}</FWLink>
                    </p>
                </div>

                {isHosted && (
                    <div>
                        <div className="title-xs">{Res.Step} 2</div>
                        <p>{Res.InstallCredProvider_Preamble}</p>
                        <p>
                            <FWLink id="2099625">{Res.InstallCredProvider}</FWLink>
                        </p>
                    </div>
                )}
            </React.Fragment>
        );
    } else {
        return (
            <React.Fragment>
                <div>
                    {Res.GetTool_NuGet_VersionPrerequisite}
                    <p>{Res.GetTool_NuGet_VisualStudioInstalled}</p>
                    <p>
                        <FormatComponent format={Res.GetTool_NuGet_PreambleNote}>
                            <FWLink id="2104208">{Res.OnlineDocumentation}</FWLink>
                        </FormatComponent>
                    </p>
                    <p>
                        <FWLink id="2099732">{Res.DownloadNuGet}</FWLink>
                    </p>
                </div>
            </React.Fragment>
        );
    }
};

GetToolComponents[ToolId.VisualStudio] = () => (
    <React.Fragment>
        <div>
            {Res.GetTool_VisualStudio_Preamble}
            <p>
                <FWLink id="2103696">{Res.DownloadVisualStudio}</FWLink>
            </p>
        </div>
    </React.Fragment>
);

GetToolComponents[ToolId.npm] = ({ userIsMember, isHosted }) => {
    const [selectedTabId, setSelectedTabId] = React.useState<string>(OperatingSystem.Windows);
    if (userIsMember) {
        return (
            <React.Fragment>
                <TabBar onSelectedTabChanged={setSelectedTabId} selectedTabId={selectedTabId} tabSize={TabSize.Compact}>
                    <Tab name="Windows" id={OperatingSystem.Windows} iconProps={{ iconName: "WindowsLogo" }} />
                    <Tab name={Res.Other} id={OperatingSystem.Other} iconProps={{ iconName: "PC1" }} />
                </TabBar>
                {selectedTabId === OperatingSystem.Windows ? (
                    <React.Fragment>
                        <div>{isHosted ? Res.GetTool_Npm_Preamble : Res.GetTool_Npm_OnPremPreamble}</div>

                        <div>
                            <div className="title-xs">{Res.Step} 1</div>
                            <p>
                                <FWLink id="2103882">{Res.DownloadNodeNpm}</FWLink>
                            </p>
                        </div>

                        {isHosted && (
                            <React.Fragment>
                                <div>
                                    <div className="title-xs">{Res.Step} 2</div>
                                    <p>{Res.RunCommand}</p>
                                    <CopyableCode>
                                        npm install -g vsts-npm-auth --registry https://registry.npmjs.com --always-auth
                                        false
                                    </CopyableCode>
                                </div>
                                <div className="body-s">
                                    {Res.CantUseCredHelper} <br />
                                    <FWLink id="2103971">{Res.HowToUsePAT}</FWLink>
                                </div>
                            </React.Fragment>
                        )}
                    </React.Fragment>
                ) : (
                    <React.Fragment>
                        <div>{isHosted ? Res.GetTool_Npm_Preamble : Res.GetTool_Npm_OnPremPreamble}</div>

                        <div>
                            <div className="title-xs">{Res.Step} 1</div>
                            <p>
                                <FWLink id="2103882">{Res.DownloadNodeNpm}</FWLink>
                            </p>
                        </div>

                        <div>
                            <div className="title-xs">{Res.Step} 2</div>
                            <p>
                                <FWLink id="2103971">{Res.HowToUsePAT}</FWLink>
                            </p>
                        </div>
                    </React.Fragment>
                )}
            </React.Fragment>
        );
    } else {
        return (
            <React.Fragment>
                <div>
                    {Res.GetTool_NpmOnly_Preamble}
                    <p>
                        <FWLink id="2103882">{Res.DownloadNodeNpm}</FWLink>
                    </p>
                </div>
            </React.Fragment>
        );
    }
};

GetToolComponents[ToolId.Maven] = () => (
    <React.Fragment>
        <div>{Res.GetTool_Maven_Preamble}</div>

        <div>
            <div className="title-xs">{Res.Step} 1</div>
            <p>
                <FWLink id="2103970">{Res.DownloadMaven}</FWLink>
            </p>
        </div>

        <div>
            <div className="title-xs">{Res.Step} 2</div>
            <p>
                <FWLink id="2103881">{Res.InstallMaven}</FWLink>
            </p>
        </div>
    </React.Fragment>
);

GetToolComponents[ToolId.Gradle] = ({ userIsMember }) => {
    if (userIsMember) {
        return (
            <React.Fragment>
                <div>{Res.GetTool_Gradle_Preamble}</div>

                <div>
                    <div className="title-xs">{Res.Step} 1</div>
                    <p>
                        <FWLink id="2103880">{Res.InstallGradle}</FWLink>
                    </p>
                </div>

                <div>
                    <div className="title-xs">{Res.Step} 2</div>
                    <p>{Res.AddMavenSettings}</p>
                    <CopyableCode>
                        <pre>{`plugins {
  id "net.linguica.maven-settings" version "0.5"
}`}</pre>
                    </CopyableCode>
                </div>
            </React.Fragment>
        );
    } else {
        return (
            <React.Fragment>
                <div>
                    {Res.GetTool_Gradle_Preamble}
                    <p>
                        <FWLink id="2103880">{Res.InstallGradle}</FWLink>
                    </p>
                </div>
            </React.Fragment>
        );
    }
};

GetToolComponents[ToolId.pip] = ({ userIsMember }) => {
    if (userIsMember) {
        return (
            <React.Fragment>
                <div>{Res.GetTool_Pip_Preamble}</div>

                <div>
                    <div className="title-xs">{Res.Step} 1</div>
                    <p>
                        <FWLink id="2103969">{Res.DownloadPython}</FWLink>
                    </p>
                </div>

                <div>
                    <div className="title-xs">{Res.Step} 2</div>
                    <p>{Res.UpdatePip}</p>
                    <CopyableCode>python -m pip install --upgrade pip</CopyableCode>
                </div>

                <div>
                    <div className="title-xs">{Res.Step} 3</div>
                    <p>{Res.InstallKeyring}</p>
                    <CopyableCode>pip install keyring artifacts-keyring</CopyableCode>
                </div>

                <div>
                    <div className="title-xs">{Res.Step} 4</div>
                    <p>
                        <FormatComponent format={Res.InstallKeyring_Linux}>
                            <FWLink underline id="2103695">
                                {Res.Prerequisites}
                            </FWLink>
                            ,
                        </FormatComponent>
                    </p>
                </div>
            </React.Fragment>
        );
    } else {
        return (
            <React.Fragment>
                <div>{Res.GetTool_PipOnly_Preamble}</div>

                <div>
                    <div className="title-xs">{Res.Step} 1</div>
                    <p>
                        <FWLink id="2103969">{Res.DownloadPython}</FWLink>
                    </p>
                </div>

                <div>
                    <div className="title-xs">{Res.Step} 2</div>
                    <p>{Res.UpdatePip}</p>
                    <CopyableCode>python -m pip install --upgrade pip</CopyableCode>
                </div>
            </React.Fragment>
        );
    }
};

GetToolComponents[ToolId.twine] = () => (
    <React.Fragment>
        <div>{Res.GetTool_Twine_Preamble}</div>

        <div>
            <div className="title-xs">{Res.Step} 1</div>
            <p>
                <FWLink id="2103969">{Res.DownloadPython}</FWLink>
            </p>
        </div>

        <div>
            <div className="title-xs">{Res.Step} 2</div>
            <p>{Res.InstallTwine}</p>
            <CopyableCode>pip install twine keyring artifacts-keyring</CopyableCode>
        </div>

        <div>
            <div className="title-xs">{Res.Step} 3</div>
            <p>
                <FormatComponent format={Res.InstallKeyring_Linux}>
                    <FWLink underline id="2103695">
                        {Res.Prerequisites}
                    </FWLink>
                    ,
                </FormatComponent>
            </p>
        </div>
    </React.Fragment>
);

GetToolComponents[ToolId.UniversalPackages] = () => (
    <React.Fragment>
        <div>{Res.GetTool_UPack_Preamble}</div>

        <div>
            <div className="title-xs">{Res.Step} 1</div>
            <p>
                <FWLink id="2103879">{Res.InstallAzureCLI}</FWLink>
            </p>
        </div>

        <div>
            <div className="title-xs">{Res.Step} 2</div>
            <p>{Res.InstallADOExtension}</p>
            <CopyableCode>az extension add --name azure-devops</CopyableCode>
        </div>
    </React.Fragment>
);

GetToolComponents[ToolId.Ivy] = () => (
    <React.Fragment>
        <div>{Res.GetTool_Ivy_Preamble}</div>
    </React.Fragment>
);
